/* THE STICKY BAR'S OWN BEHAVIOUR. NEW FILE, 2026-09-19, added by the final regression suite of the UI-polish pass.

   WHY IT EXISTS. Brief L requires "keyboard focus is never covered by the sticky bar". The final suite measured one
   focusable that IS covered on the FaucetPro PDP: the `mailto:support@pureverr.com` link in the buy box, focused rect
   872.7-889.7 under a bar spanning 844-909 at a 390x844 viewport. `scroll-padding-bottom` cannot fix it, and that is
   not a bug in the CSS - the property only constrains scrolls the BROWSER performs to bring an off-screen element into
   view. This link is already inside the viewport, so focusing it scrolls nothing and it simply sits under the bar
   (WCAG 2.2 SC 2.4.11; axe does not test this, which is why three clean axe runs never mentioned it).
   The fix the theme playbook prescribes for exactly this case is a `focusin` nudge, and that is what this is.

   WHY IT IS ITS OWN FILE, and not another line in an existing one. The bar is a SHARED component
   (assets/pureverr-sticky.css) rendered by `sections/main-product.liquid` and `sections/pureverr-filterpro-main.liquid`,
   which between them serve every template carrying an enabled `sticky_atc` block - including `templates/product.json`,
   used by the DUO bundle page, where NEITHER pureverr-offer.js NOR pureverr-filterpro.js runs. Putting the behaviour in
   either of those would have left the generic template uncovered. A component that makes a promise should keep it
   itself.

   IT ALSO MAKES THE DOCUMENTED MECHANISM TRUE EVERYWHERE. pureverr-sticky.css says the bar's height is published as
   `--pv-sticky-h` and `html` takes it as `scroll-padding-bottom`. That was only true on the FaucetPro PDP: the variable
   was written by pureverr-offer.js, which FilterPro does not load (FilterPro reached the same outcome by setting
   `scrollPaddingBottom` inline instead, and the generic template did neither). The independent reviewer caught the
   wording; this makes the wording correct. Nothing is removed - the two existing writers set the same values, so they
   agree rather than fight.

   Presentation only. It never submits, never touches the cart, and never moves focus - it moves the PAGE, by the
   smallest amount that uncovers the focused element. If this file fails to load, the bar behaves exactly as it does
   today. */
(function () {
  'use strict';
  if (window.__pvSticky) return;
  window.__pvSticky = true;

  var reduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var MARGIN = 12; /* matches the +12px in pureverr-sticky.css's scroll-padding-bottom */

  /* TWO DIFFERENT QUESTIONS, and conflating them cost a measurement.
     `measurableBars` - does this page HAVE a bar with a height? That is the condition the stylesheet itself uses
       (`html:has(.pv-stickybar)`), so it is the condition the height must be published under.
     `barsInView`     - is a bar actually in front of the reader right now? Only that can cover something, so only
       that drives the nudge.
     The first version used the in-view test for BOTH. On templates/product.json (the DUO bundle page) the bar sits
     parked at `top: 844` in an 844 px viewport - it exists, it is 65 px tall, and it is exactly one pixel-row below
     the fold until the reveal transform runs. So the in-view test correctly said "not in view", the height was never
     published, and `scroll-padding-bottom` computed to `calc(0px + 12px)` = 12 px against a 65 px bar. Measured, not
     reasoned: `{ h: 65, top: 844, innerH: 844, passesFilter: false }`. */
  function measurableBars() {
    return [].slice.call(document.querySelectorAll('.pv-stickybar')).filter(function (b) {
      return b.offsetHeight > 0;
    });
  }
  function barsInView() {
    return measurableBars().filter(function (b) {
      var r = b.getBoundingClientRect();
      return r.bottom > 0 && r.top < window.innerHeight && getComputedStyle(b).visibility !== 'hidden';
    });
  }

  /* --- publish the height ---------------------------------------------------------------------------------------
     CORRECTED after the first version measured `--pv-sticky-h: ''` on two of the three pages. The bar is REVEALED BY
     SCROLL (`sticky-atc[data-after-scroll]`, and FilterPro's own show/hide), so at boot it has no height, and the
     first version measured once, found nothing, and never looked again - it also never attached its ResizeObserver,
     because it attached it only when it had already found a bar. It now keeps trying until it succeeds, on scroll and
     on resize, and only then observes. */
  var ro = null, published = false, ticking = false;
  function publish() {
    ticking = false;
    var b = measurableBars()[0];
    if (!b) return;
    var h = b.offsetHeight;
    if (h > 0) { document.documentElement.style.setProperty('--pv-sticky-h', h + 'px'); published = true; }
    if (!ro && 'ResizeObserver' in window) {
      ro = new ResizeObserver(function () { publish(); });
      try { ro.observe(b); } catch (e) { ro = null; }
    }
  }
  function schedulePublish() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(publish);
  }

  /* --- the nudge -------------------------------------------------------------------------------------------------
     CORRECTED after a control run proved the first version never fired. It gated on `el.matches(':focus-visible')`,
     and a focus set from script does NOT match `:focus-visible` in Chrome unless the last real interaction was a
     keyboard one - so the guard returned early every time, including in the very test written to check it. The intent
     was "keyboard, not mouse", so that is now what is measured: a focus that arrives within 300 ms of a pointerdown
     is a click and is left alone; anything else is treated as keyboard and is uncovered. This also makes the
     behaviour testable, which the first version was not. */
  var lastPointer = 0;
  ['pointerdown', 'mousedown', 'touchstart'].forEach(function (t) {
    document.addEventListener(t, function () { lastPointer = Date.now(); }, { capture: true, passive: true });
  });

  function overlapPx(el) {
    var b = null, i, list = barsInView();
    for (i = 0; i < list.length; i++) if (!list[i].contains(el)) { b = list[i]; break; }
    if (!b) return 0;
    var r = el.getBoundingClientRect(), br = b.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return 0;
    if (r.right <= br.left || r.left >= br.right) return 0;   /* the bar is not in front of it */
    var under = r.bottom + MARGIN - br.top;
    return under > 0 ? under : 0;
  }

  function onFocusIn(e) {
    var el = e.target;
    if (!el || !el.getBoundingClientRect) return;
    if (Date.now() - lastPointer < 300) return;               /* a click, not a Tab */
    var by = overlapPx(el);
    if (by <= 0) return;
    var max = document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
    by = Math.min(by, Math.max(0, max));
    if (by <= 0) return;
    /* `auto`, not `smooth`: a smooth scroll is still travelling when the next Tab arrives, so a fast tabbing user
       measures (and sees) the old position. Instant is also what reduced-motion wants. */
    window.scrollBy({ top: by, left: 0, behavior: 'auto' });
  }

  function boot() {
    schedulePublish();
    document.addEventListener('focusin', onFocusIn);
    window.addEventListener('scroll', schedulePublish, { passive: true });
    window.addEventListener('resize', schedulePublish, { passive: true });
    ['shopify:section:load', 'shopify:section:select', 'shopify:section:deselect'].forEach(function (t) {
      document.addEventListener(t, function () { ro = null; published = false; schedulePublish(); });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
